import {Outlet, useLocation} from "react-router-dom";
import {GoogleOAuthProvider} from '@react-oauth/google';

const HEADLINE_GRADIENT = {
	background: 'linear-gradient(to right, #5A0AE0, #8C2CE2, #3b82f6)',
	WebkitBackgroundClip: 'text',
	WebkitTextFillColor: 'transparent',
	backgroundClip: 'text',
};

// Floating language chips — AudioDub's 4 currently-supported languages
// (English, Hindi, Cantonese, Mandarin — same set the Auto-Dub setup offers).
// Repeated across positions to keep the panel lively.
const FLOAT_TAGS = [
	{label: 'EN', left: '16%', top: '20%', delay: '0s'},      // English
	{label: '中文', left: '70%', top: '12%', delay: '1.6s'},   // Mandarin
	{label: 'हिन्दी', left: '58%', top: '80%', delay: '3.2s'}, // Hindi
	{label: '粵語', left: '9%', top: '58%', delay: '0.8s'},     // Cantonese
	{label: 'EN', left: '84%', top: '54%', delay: '2.0s'},     // English
	{label: '中文', left: '38%', top: '90%', delay: '2.4s'},   // Mandarin
	{label: 'हिन्दी', left: '89%', top: '26%', delay: '1.2s'}, // Hindi
	{label: '粵語', left: '23%', top: '74%', delay: '2.8s'},    // Cantonese
	{label: '中文', left: '90%', top: '70%', delay: '1.0s'},   // Mandarin
	{label: 'EN', left: '7%', top: '38%', delay: '3.6s'},      // English
];

// Bar heights (px) for the live waveform card
const WAVE_HEIGHTS = [3, 6, 9, 5, 8, 4, 7, 9, 3, 6, 8, 5, 7, 4, 9, 6, 3, 8, 5, 7];

// The YouTube Auto-Dub pipeline — mirrors the 4-step setup flow
// (Source channel → Pick videos → Languages & voices → Go live).
const FLOW_STEPS = [
	{icon: 'fa-brands fa-youtube', label: 'Connect'},
	{icon: 'fa-solid fa-clapperboard', label: 'Videos'},
	{icon: 'fa-solid fa-language', label: 'Dub'},
	{icon: 'fa-solid fa-rocket', label: 'Publish'},
];

// Left-panel intro shown on password-recovery routes (forgot / reset).
function RecoveryIntro() {
	return (
		<>
			<p className="auth-fade-1 text-primary text-[11px] font-bold uppercase tracking-[0.28em] mb-4">
				Account recovery
			</p>
			<h2 className="auth-fade-2 font-sans font-black text-slate-900 leading-[1.02] tracking-tight mb-6"
			    style={{fontSize: 'clamp(40px, 4vw, 56px)'}}>
				Locked out?<br/>
				<span style={HEADLINE_GRADIENT}>We&apos;ve got you.</span>
			</h2>
			<p className="auth-fade-3 text-slate-500 text-[15px] leading-relaxed max-w-md mb-10">
				Enter your email and we&apos;ll send a secure link to reset your password in seconds.
			</p>
			<div className="auth-fade-4 flex items-center gap-3 p-4 rounded-2xl border border-violet-200 bg-white shadow-sm w-fit">
				<svg className="w-5 h-5 text-primary flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
					<path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
				</svg>
				<span className="text-sm text-slate-600">Secure, single-use reset link</span>
			</div>
		</>
	);
}

// Left-panel intro shown while setting a brand-new password (reset route).
function ResetIntro() {
	return (
		<>
			<p className="auth-fade-1 text-primary text-[11px] font-bold uppercase tracking-[0.28em] mb-4">
				Account security
			</p>
			<h2 className="auth-fade-2 font-sans font-black text-slate-900 leading-[1.02] tracking-tight mb-6"
			    style={{fontSize: 'clamp(40px, 4vw, 56px)'}}>
				New password.<br/>
				<span style={HEADLINE_GRADIENT}>Fresh start.</span>
			</h2>
			<p className="auth-fade-3 text-slate-500 text-[15px] leading-relaxed max-w-md mb-10">
				Pick a strong one and you&apos;re back to dubbing in seconds — no link to wait on.
			</p>
			<div className="auth-fade-4 flex items-center gap-3 p-4 rounded-2xl border border-violet-200 bg-white shadow-sm w-fit">
				<svg className="w-5 h-5 text-primary flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
					<path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
				</svg>
				<span className="text-sm text-slate-600">Encrypted &amp; never stored in plain text</span>
			</div>
		</>
	);
}

export default function AuthLayout() {
	const {pathname} = useLocation();
	const isReset = pathname.includes('reset-password');
	const isRecovery = pathname.includes('forgot-password') || isReset;
	return (
		<div className="flex min-h-screen overflow-hidden bg-white text-slate-900 font-primary">

			{/* ══ LEFT PANEL ══ */}
			<div className="hidden lg:flex flex-col w-[55%] relative overflow-hidden bg-[#f5f3ff]">
				{/* animated radial wash */}
				<div className="absolute inset-0 auth-grad-anim"
				     style={{background: 'radial-gradient(ellipse 80% 60% at 30% 40%, rgba(90,10,224,0.12) 0%, transparent 60%), radial-gradient(ellipse 60% 80% at 70% 60%, rgba(37,99,235,0.07) 0%, transparent 60%)'}}/>
				{/* grid texture */}
				<div className="absolute inset-0 opacity-[0.06]"
				     style={{backgroundImage: 'linear-gradient(rgba(90,10,224,1) 1px, transparent 1px), linear-gradient(90deg, rgba(90,10,224,1) 1px, transparent 1px)', backgroundSize: '60px 60px'}}/>

				{/* floating language tags */}
				<div className="absolute inset-0 pointer-events-none">
					{FLOAT_TAGS.map((t, i) => (
						<span key={i}
						      className="auth-float-tag absolute px-3 py-1.5 rounded-full text-[11px] font-semibold text-primary border border-violet-200 bg-white/70 backdrop-blur-sm select-none shadow-sm"
						      style={{left: t.left, top: t.top, animationDelay: t.delay}}>
							{t.label}
						</span>
					))}
				</div>

				<div className="relative z-10 flex flex-col h-full px-16 py-14">
					<a href="/" className="mb-auto">
						<img src="/images/logo-full.png" alt="AudioDub.ai" className="h-8 w-auto object-contain"/>
					</a>

					<div className="flex-1 flex flex-col justify-center">
						{isRecovery ? (isReset ? <ResetIntro/> : <RecoveryIntro/>) : <>
						{/* cards row */}
						<div className="order-last mt-10 flex gap-4 w-full max-w-[640px] flex-wrap">
						{/* waveform card */}
						<div className="auth-fade-1 flex-1 min-w-[240px] flex flex-col justify-center rounded-3xl border border-violet-200 bg-white shadow-lg p-6 relative overflow-hidden">
							<div className="absolute inset-0 bg-gradient-to-br from-violet-50 to-transparent"/>
							<div className="relative z-10">
								<div className="flex items-center gap-2 mb-3">
									<div className="w-2 h-2 rounded-full bg-green-500" style={{boxShadow: '0 0 8px rgba(34,197,94,0.5)'}}/>
									<span className="text-[11px] font-semibold text-slate-700">Live dubbing</span>
								</div>
								<div className="flex items-end gap-[3px] h-10 w-full">
									{WAVE_HEIGHTS.map((h, i) => (
										<div key={i} className="auth-wave-bar"
										     style={{height: `${h * 4}px`, animationDelay: `${i * 0.06}s`}}/>
									))}
								</div>
								<p className="text-xs text-slate-600 mt-3">Translating · English → Cantonese</p>
							</div>
						</div>

						{/* YouTube Auto-Dub flow card */}
						<div className="auth-fade-2 flex-1 min-w-[240px] flex flex-col justify-center rounded-3xl border border-violet-200 bg-white shadow-lg p-6 relative overflow-hidden">
							<div className="absolute inset-0 bg-gradient-to-br from-violet-50 to-transparent"/>
							<div className="relative z-10">
								<div className="flex items-center gap-2 mb-5">
									<span className="grid place-items-center w-6 h-6 rounded-md bg-youtube/10 text-youtube">
										<i className="fa-brands fa-youtube text-[13px]"/>
									</span>
									<span className="text-[11px] font-semibold text-slate-700">YouTube Auto-Dub</span>
									<span className="ml-auto text-[9px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded-full">Automated</span>
								</div>

								{/* step icons with a connecting rail */}
								<div className="relative flex justify-between">
									<div className="absolute top-5 left-5 right-5 h-[2px] bg-violet-100"/>
									{FLOW_STEPS.map((s, i) => (
										<span key={i}
										      className="auth-flow-step relative z-10 w-10 h-10 rounded-xl grid place-items-center bg-[#f1edff] text-primary"
										      style={{animationDelay: `${i * 1.1}s`}}>
											<i className={`${s.icon} text-[15px]`}/>
										</span>
									))}
								</div>
								<div className="flex justify-between mt-2">
									{FLOW_STEPS.map((s, i) => (
										<span key={i} className="w-10 text-center text-[10px] font-medium text-slate-500">{s.label}</span>
									))}
								</div>
							</div>
						</div>
						</div>

						<p className="auth-fade-1 text-primary text-[11px] font-bold uppercase tracking-[0.28em] mb-4">
							AI Dubbing Studio
						</p>
						<h2 className="auth-fade-2 font-sans font-black text-slate-900 leading-[1.02] tracking-tight mb-6"
						    style={{fontSize: 'clamp(40px, 4vw, 56px)'}}>
							Dub smarter.<br/>
							<span style={{background: 'linear-gradient(to right, #5A0AE0, #8C2CE2, #3b82f6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'}}>
								Reach further.
							</span>
						</h2>
						<p className="auth-fade-3 text-slate-500 text-[15px] leading-relaxed max-w-md mb-10">
							Natural AI voices in multiple languages — emotion-aware and studio-ready in minutes.
						</p>
						<div className="auth-fade-4 flex items-center gap-4 flex-wrap">
							<span className="text-[12px] text-slate-500 font-medium">✓ Multiple languages</span>
							<span className="text-[12px] text-slate-500 font-medium">✓ Natural AI voices</span>
							<span className="text-[12px] text-slate-500 font-medium">✓ YouTube auto dub</span>
						</div>
						</>}
					</div>

					<p className="text-slate-400 text-xs">© {new Date().getFullYear()} AudioDub.ai</p>
				</div>
			</div>

			{/* ══ RIGHT PANEL (form) ══ */}
			<div className="flex-1 flex flex-col justify-center items-center px-6 py-12 bg-white relative">
				<div className="absolute top-0 right-0 w-96 h-96 rounded-full pointer-events-none"
				     style={{background: 'rgba(90,10,224,0.04)', filter: 'blur(100px)'}}/>
				<div className="absolute bottom-0 left-0 w-64 h-64 rounded-full pointer-events-none"
				     style={{background: 'rgba(59,130,246,0.04)', filter: 'blur(80px)'}}/>

				<a href="/" className="lg:hidden absolute top-8 left-8">
					<img src="/images/logo-full.png" alt="AudioDub.ai" className="h-7 w-auto"/>
				</a>

				<div className="w-full max-w-[400px] relative z-10">
					<GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
						<Outlet/>
					</GoogleOAuthProvider>
				</div>
			</div>
		</div>
	);
}
